/**
 * 统一视频缓存管理器
 * 解决缓存键不一致和重复缓存问题
 * 所有视频缓存都使用统一的 ${userId}_${songId}_video 格式
 */

import videoCacheService from './VideoCacheService.js';
import smartCacheManager from './SmartCacheManager.js';

/**
 * 视频版歌曲：播放时是否自动缓存（整文件 / HLS 分片边播边存）。
 * false = 仅用户手动「缓存」才会写入本地；图集内短视频 HLS 分片缓存不受此开关影响。
 */
export const VIDEO_SONG_AUTO_CACHE_ENABLED = false;

export function isVideoSongAutoCacheEnabled() {
  return VIDEO_SONG_AUTO_CACHE_ENABLED;
}

class UnifiedVideoCacheManager {
  constructor() {
    this.cacheStates = new Map(); // 全局缓存状态管理
    this.cacheListeners = new Map(); // 缓存状态监听器
  }

  /**
   * 获取当前用户ID
   */
  getCurrentUserId() {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.sub;
      }
    } catch (error) {
      console.warn('⚠️ [统一视频缓存] 获取用户ID失败:', error);
    }
    return null;
  }

  /**
   * 生成统一的视频缓存键
   */
  generateVideoCacheKey(userId, songId) {
    return `${userId}_${songId}_video`;
  }

  /**
   * 检查视频是否已缓存（统一检查）
   */
  async isVideoCached(userId, songId) {
    console.log(`🎬 [统一视频缓存] 开始检查视频缓存:`, {
      userId,
      songId,
      timestamp: new Date().toISOString()
    });

    if (!userId || !songId) {
      console.warn('⚠️ [统一视频缓存] 用户ID或歌曲ID无效:', { userId, songId });
      return false;
    }

    const cacheKey = this.generateVideoCacheKey(userId, songId);
    console.log(`🎬 [统一视频缓存] 生成的缓存键: ${cacheKey}`);
    
    // 检查全局缓存状态
    if (this.cacheStates.has(cacheKey)) {
      const state = this.cacheStates.get(cacheKey);
      console.log(`🎬 [统一视频缓存] 全局状态检查:`, {
        cacheKey,
        status: state.status,
        timestamp: state.timestamp
      });
      
      if (state.status === 'cached') {
        console.log('🎬 [统一视频缓存] ✅ 从全局状态找到缓存:', songId);
        return true;
      }
    } else {
      console.log(`🎬 [统一视频缓存] 全局状态中未找到缓存键: ${cacheKey}`);
    }

    // 检查完整视频缓存
    console.log(`🎬 [统一视频缓存] 开始检查完整视频缓存...`);
    try {
      const fullCache = await videoCacheService.hasFullVideoCache(userId, songId);
      console.log(`🎬 [统一视频缓存] 完整视频缓存检查结果:`, {
        hasFullCache: fullCache,
        userId,
        songId
      });
      
      if (fullCache) {
        console.log('🎬 [统一视频缓存] ✅ 找到完整视频缓存:', songId);
        this.updateCacheState(cacheKey, 'cached', fullCache);
        return true;
      }
    } catch (error) {
      console.error('🎬 [统一视频缓存] ❌ 检查完整视频缓存失败:', error);
    }

    // 检查部分视频缓存
    console.log(`🎬 [统一视频缓存] 开始检查部分视频缓存...`);
    try {
      const partialCache = await videoCacheService.hasPartialVideoCache(userId, songId);
      console.log(`🎬 [统一视频缓存] 部分视频缓存检查结果:`, {
        hasPartialCache: partialCache,
        userId,
        songId
      });
      
      if (partialCache) {
        console.log('🎬 [统一视频缓存] ✅ 找到部分视频缓存:', songId);
        this.updateCacheState(cacheKey, 'partial', partialCache);
        return true;
      }
    } catch (error) {
      console.error('🎬 [统一视频缓存] ❌ 检查部分视频缓存失败:', error);
    }

    console.log('🎬 [统一视频缓存] ❌ 视频未缓存:', songId);
    this.updateCacheState(cacheKey, 'not_cached', null);
    return false;
  }

  /**
   * 更新全局缓存状态
   */
  updateCacheState(cacheKey, status, data) {
    this.cacheStates.set(cacheKey, {
      status,
      data,
      timestamp: Date.now()
    });

    // 通知监听器
    if (this.cacheListeners.has(cacheKey)) {
      const listeners = this.cacheListeners.get(cacheKey);
      listeners.forEach(listener => {
        try {
          listener(status, data);
        } catch (error) {
          console.error('❌ [统一视频缓存] 监听器执行失败:', error);
        }
      });
    }
  }

  /**
   * 添加缓存状态监听器
   */
  addCacheListener(userId, songId, listener) {
    const cacheKey = this.generateVideoCacheKey(userId, songId);
    
    if (!this.cacheListeners.has(cacheKey)) {
      this.cacheListeners.set(cacheKey, []);
    }
    
    this.cacheListeners.get(cacheKey).push(listener);
    
    // 返回取消监听的函数
    return () => {
      const listeners = this.cacheListeners.get(cacheKey);
      if (listeners) {
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  /**
   * 智能缓存视频（统一缓存入口）
   */
  async smartCacheVideo(userId, songId, videoElement, progressCallback) {
    if (!isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [统一视频缓存] 视频版自动缓存已关闭，跳过 smartCacheVideo:', songId);
      return false;
    }
    if (!userId || !songId || !videoElement) {
      console.warn('⚠️ [统一视频缓存] 参数无效');
      return false;
    }

    const cacheKey = this.generateVideoCacheKey(userId, songId);
    
    // 检查是否已经在缓存中
    if (await this.isVideoCached(userId, songId)) {
      console.log('🎬 [统一视频缓存] 视频已缓存，跳过:', songId);
      return true;
    }

    // 标记为正在缓存
    this.updateCacheState(cacheKey, 'caching', null);

    try {
      console.log('🎬 [统一视频缓存] 开始智能缓存视频:', songId);
      
      // 使用VideoCacheService进行智能缓存
      const result = await videoCacheService.smartCacheVideo(
        userId,
        songId,
        videoElement,
        progressCallback
      );

      if (result) {
        console.log('✅ [统一视频缓存] 视频缓存成功:', songId);
        this.updateCacheState(cacheKey, 'cached', result);
        return true;
      } else {
        console.log('❌ [统一视频缓存] 视频缓存失败:', songId);
        this.updateCacheState(cacheKey, 'failed', null);
        return false;
      }
    } catch (error) {
      console.error('❌ [统一视频缓存] 视频缓存异常:', error);
      this.updateCacheState(cacheKey, 'failed', null);
      return false;
    }
  }

  /**
   * 启动实时缓存（统一实时缓存入口）
   */
  async startRealTimeCache(userId, songId, videoElement, options) {
    if (!isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [统一视频缓存] 视频版自动缓存已关闭，跳过 startRealTimeCache:', songId);
      return null;
    }
    if (!userId || !songId || !videoElement) {
      console.warn('⚠️ [统一视频缓存] 参数无效');
      return null;
    }

    const cacheKey = this.generateVideoCacheKey(userId, songId);
    
    // 检查是否已经在缓存中
    if (await this.isVideoCached(userId, songId)) {
      console.log('🎬 [统一视频缓存] 视频已缓存，跳过实时缓存:', songId);
      return null;
    }

    try {
      console.log('🎬 [统一视频缓存] 启动实时缓存:', songId);
      
      // 使用VideoCacheService进行实时缓存
      const result = await videoCacheService.startRealTimeCache(
        userId,
        songId,
        videoElement,
        options
      );

      if (result) {
        console.log('✅ [统一视频缓存] 实时缓存启动成功:', songId);
        this.updateCacheState(cacheKey, 'real_time_caching', result);
      }

      return result;
    } catch (error) {
      console.error('❌ [统一视频缓存] 实时缓存启动失败:', error);
      return null;
    }
  }

  /**
   * 获取缓存的视频数据
   */
  async getCachedVideo(userId, songId) {
    if (!userId || !songId) {
      return null;
    }

    console.log('🎬 [统一视频缓存] 开始获取缓存视频:', { userId, songId });

    // 先检查完整缓存
    const fullCache = await videoCacheService.getFullVideoCache(userId, songId);
    if (fullCache) {
      console.log('🎬 [统一视频缓存] 返回完整视频缓存:', songId);
      return fullCache;
    }

    // 再检查部分缓存
    const partialCache = await videoCacheService.getPartialVideoCache(userId, songId);
    if (partialCache) {
      console.log('🎬 [统一视频缓存] 返回部分视频缓存:', songId);
      return partialCache;
    }

    console.log('🎬 [统一视频缓存] 未找到任何视频缓存:', songId);
    return null;
  }

  /**
   * 清理缓存状态
   */
  clearCacheState(userId, songId) {
    const cacheKey = this.generateVideoCacheKey(userId, songId);
    this.cacheStates.delete(cacheKey);
    this.cacheListeners.delete(cacheKey);
  }

  /**
   * 获取所有缓存状态
   */
  getAllCacheStates() {
    return Array.from(this.cacheStates.entries()).map(([key, state]) => ({
      cacheKey: key,
      ...state
    }));
  }

  /**
   * 缓存已播放的部分
   */
  async cachePlayedRange(userId, songId, videoElement, currentTime, options) {
    if (!userId || !songId || !videoElement) {
      console.warn('⚠️ [统一视频缓存] 参数无效');
      return false;
    }

    try {
      console.log('🎬 [统一视频缓存] 缓存已播放部分:', songId, '时间:', currentTime);
      
      // 使用VideoCacheService进行部分缓存
      const result = await videoCacheService.cachePlayedRange(
        userId,
        songId,
        videoElement,
        currentTime,
        options
      );

      if (result) {
        const cacheKey = this.generateVideoCacheKey(userId, songId);
        this.updateCacheState(cacheKey, 'partial', result);
      }

      return result;
    } catch (error) {
      console.error('❌ [统一视频缓存] 缓存已播放部分失败:', error);
      return false;
    }
  }

  /**
   * 迁移现有缓存到统一格式
   */
  async migrateExistingCache() {
    try {
      console.log('🔄 [统一视频缓存] 开始迁移现有缓存...');
      
      // 迁移完整视频缓存
      await this.migrateFullVideoCache();
      
      // 迁移部分视频缓存
      await this.migratePartialVideoCache();
      
      console.log('✅ [统一视频缓存] 缓存迁移完成');
    } catch (error) {
      console.error('❌ [统一视频缓存] 缓存迁移失败:', error);
    }
  }

  /**
   * 迁移完整视频缓存
   */
  async migrateFullVideoCache() {
    try {
      // 获取所有完整视频缓存
      const fullCacheKeys = await videoCacheService._dbGetAllKeys('video-files');
      
      for (const oldKey of fullCacheKeys) {
        // 检查是否是旧格式的缓存键
        if (oldKey.includes('_full') || oldKey.includes('_video')) {
          const parts = oldKey.split('_');
          if (parts.length >= 3) {
            const userId = parts[0];
            const songId = parts[1];
            const newKey = this.generateVideoCacheKey(userId, songId);
            
            // 如果新键不存在，则迁移数据
            const existingData = await videoCacheService._dbGet('video-files', newKey);
            if (!existingData) {
              const oldData = await videoCacheService._dbGet('video-files', oldKey);
              if (oldData) {
                // 迁移到新键
                await videoCacheService._dbPut('video-files', newKey, oldData);
                console.log('🔄 [统一视频缓存] 迁移完整缓存:', oldKey, '->', newKey);
                
                // 删除旧键
                await videoCacheService._dbDelete('video-files', oldKey);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ [统一视频缓存] 迁移完整视频缓存失败:', error);
    }
  }

  /**
   * 迁移部分视频缓存
   */
  async migratePartialVideoCache() {
    try {
      // 获取所有部分视频缓存
      const partialCacheKeys = await videoCacheService._dbGetAllKeys('partial-video-cache');
      
      for (const oldKey of partialCacheKeys) {
        // 检查是否是旧格式的缓存键
        if (oldKey.includes('_partial') || oldKey.includes('_video')) {
          const parts = oldKey.split('_');
          if (parts.length >= 3) {
            const userId = parts[0];
            const songId = parts[1];
            const newKey = this.generateVideoCacheKey(userId, songId);
            
            // 如果新键不存在，则迁移数据
            const existingData = await videoCacheService._dbGet('partial-video-cache', newKey);
            if (!existingData) {
              const oldData = await videoCacheService._dbGet('partial-video-cache', oldKey);
              if (oldData) {
                // 迁移到新键
                await videoCacheService._dbPut('partial-video-cache', newKey, oldData);
                console.log('🔄 [统一视频缓存] 迁移部分缓存:', oldKey, '->', newKey);
                
                // 删除旧键
                await videoCacheService._dbDelete('partial-video-cache', oldKey);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ [统一视频缓存] 迁移部分视频缓存失败:', error);
    }
  }

  /**
   * 🔥 永久缓存：不再清理缓存状态，状态信息永久保存
   * 只有用户主动清理或组件销毁时才会清理状态
   */
  cleanExpiredCacheStates() {
    console.log('🎬 [统一视频缓存] 永久缓存模式：跳过缓存状态清理，状态信息永久保存');
    
    // 只清理损坏的状态，不清理过期状态
    // 这里可以添加损坏状态检测逻辑，但暂时保持所有状态
  }

  /**
   * 清除视频缓存（用于清除损坏的缓存）
   */
  async clearVideoCache(userId, songId) {
    console.log('🗑️ [统一视频缓存] 开始清除视频缓存:', { userId, songId });
    
    try {
      const cacheKey = this.generateVideoCacheKey(userId, songId);
      
      // 清除完整视频缓存
      await videoCacheService._dbDelete('video-files', cacheKey);
      console.log('🗑️ [统一视频缓存] 已清除完整视频缓存:', cacheKey);
      
      // 清除部分视频缓存
      await videoCacheService._dbDelete('partial-video-cache', cacheKey);
      console.log('🗑️ [统一视频缓存] 已清除部分视频缓存:', cacheKey);
      
      // 清除全局状态
      this.cacheStates.delete(cacheKey);
      this.cacheListeners.delete(cacheKey);
      console.log('🗑️ [统一视频缓存] 已清除全局状态:', cacheKey);
      
      console.log('✅ [统一视频缓存] 视频缓存清除完成:', { userId, songId });
      return true;
    } catch (error) {
      console.error('❌ [统一视频缓存] 清除视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 显式缓存视频文件
   * 用于 VideoController/手动触发的缓存流程，统一走 userId + songId 作为缓存键
   * @param {string|number} userId
   * @param {string|number} songId
   * @param {string} videoUrl - 实际的视频下载地址（OneDrive/CDN 等）
   * @param {Object} options - 可选：进度、完成、错误回调
   */
  async cacheVideo(userId, songId, videoUrl, options = {}) {
    if (!options.manual && !isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [统一视频缓存] 视频版自动缓存已关闭，跳过 cacheVideo:', songId);
      return false;
    }
    if (!userId || !songId || !videoUrl) {
      console.warn('⚠️ [统一视频缓存] cacheVideo 参数无效:', { userId, songId, videoUrl });
      return false;
    }

    const { onProgress, onComplete, onError } = options;

    try {
      console.log('🎬 [统一视频缓存] 开始显式缓存视频:', {
        userId,
        songId,
        videoUrl: typeof videoUrl === 'string' ? videoUrl.substring(0, 80) + '...' : ''
      });

      // 如果已经有完整缓存，直接跳过
      const existing = await videoCacheService.hasFullVideoCache(userId, songId);
      if (existing) {
        console.log('🎬 [统一视频缓存] 视频已存在完整缓存，跳过下载:', songId);
        if (typeof onComplete === 'function') {
          onComplete();
        }
        return true;
      }

      const cacheKey = this.generateVideoCacheKey(userId, songId);
      this.updateCacheState(cacheKey, 'caching', null);

      // 通过 VideoCacheService 真实下载并写入 IndexedDB
      await videoCacheService.downloadAndCacheVideo(
        videoUrl,
        userId,
        songId,
        (progress, loaded, total) => {
          if (typeof onProgress === 'function') {
            onProgress(progress, loaded, total);
          }
        }
      );

      // 下载并写入成功后，状态会在 VideoCacheService 中落盘，这里只更新内存状态
      const finalData = await videoCacheService.getFullVideoCache(userId, songId);
      this.updateCacheState(cacheKey, 'cached', finalData || null);

      console.log('✅ [统一视频缓存] 显式视频缓存完成:', songId);
      if (typeof onComplete === 'function') {
        onComplete();
      }
      return true;
    } catch (error) {
      console.error('❌ [统一视频缓存] 显式缓存视频失败:', error);
      const cacheKey = this.generateVideoCacheKey(userId, songId);
      this.updateCacheState(cacheKey, 'failed', null);
      if (typeof onError === 'function') {
        onError(error);
      }
      return false;
    }
  }
}

// 创建单例实例
const unifiedVideoCacheManager = new UnifiedVideoCacheManager();

// 初始化时执行缓存迁移
unifiedVideoCacheManager.migrateExistingCache();

// 🔥 永久缓存：不再定期清理状态，状态信息永久保存
// setInterval(() => {
//   unifiedVideoCacheManager.cleanExpiredCacheStates();
// }, 30 * 60 * 1000); // 每30分钟清理一次

export default unifiedVideoCacheManager;
